import React, { useState } from "react";
import { Helmet } from "react-helmet";
import "../css/contactus.css";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      const response = await fetch("https://getyourcscs.co.uk/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus({
          type: "success",
          message: "Thank you! Your message has been sent successfully.",
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        setStatus({
          type: "error",
          message: "Something went wrong. Please try again later.",
        });
      }
    } catch (error) {
      console.error("Contact form error:", error);
      setStatus({
        type: "error",
        message:
          "Failed to connect to the server. Please check your connection. Ensure your backend is running on port 5000.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Structured Data for Google (Get Your CSCS) ---
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Contact Get Your CSCS",
    url: "https://www.getyourcscs.co.uk/contact",
    mainEntity: {
      "@type": "LocalBusiness",
      name: "Get Your CSCS",
      telephone: "01922 123 456",
      email: "info@getyourcscs.co.uk",
      address: [
        {
          "@type": "PostalAddress",
          streetAddress: "86 High Street",
          addressLocality: "West Bromwich",
          postalCode: "B70 6JW",
          addressCountry: "UK",
        },
      ],
    },
  };

  return (
    <div className="page-wrapper flat-dark-theme">
      {/* --- SEO METADATA START --- */}
      <Helmet>
        <title>Get Your CSCS | Contact Us</title>
        <meta
          name="description"
          content="Get in touch with Get Your CSCS. Have questions about our 1-day CSCS courses, CITB test bookings, or card requirements in Walsall and West Bromwich? We're here to help."
        />
        <meta
          name="keywords"
          content="contact Get Your CSCS, CSCS course booking help, Walsall CSCS test centre contact, West Bromwich CITB contact, Get Your CSCS phone number"
        />

        {/* Open Graph / Social Media */}
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.getyourcscs.co.uk/contact"
        />
        <meta property="og:title" content="Get Your CSCS | Contact Us" />
        <meta
          property="og:description"
          content="Need help booking your CITB test or getting your CSCS card? Contact our training centres in Walsall and West Bromwich today."
        />

        {/* Canonical Link */}
        <link rel="canonical" href="https://www.getyourcscs.co.uk/contact" />

        {/* Inject Schema Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      </Helmet>
      {/* --- SEO METADATA END --- */}

      {/* 1. Sleek Hero Header */}
      <div className="contact-hero">
        <h2 className="section-title">Get in Touch with Get Your CSCS</h2>
        <p className="intro-text">
          Have a question about our CSCS card packages, need help securing a
          date, or want to discuss group training? Reach out to our team below.
        </p>
      </div>

      <div className="section-container contact-main">
        <div className="contact-flat-card flat-panel">
          {/* Left Column: Contact Information */}
          <div className="contact-info">
            <h3>Contact Details</h3>

            <div className="info-block">
              <div className="info-item">
                <span className="icon">📞</span>
                <div>
                  <h4>Phone</h4>
                  <p>01922 123 456</p>
                </div>
              </div>

              <div className="info-item">
                <span className="icon">✉️</span>
                <div>
                  <h4>Email</h4>
                  <p>admin@getyourcscs.co.uk</p>
                </div>
              </div>

              <div className="info-item">
                <span className="icon">📍</span>
                <div>
                  <h4>West Bromwich Centre</h4>
                  <p>86 High Street, West Bromwich, B70 6JW</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="contact-form-section">
            <h3>Send us a message</h3>

            <form onSubmit={handleFormSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                  />
                </div>
                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="07123 456789"
                  />
                </div>
                <div className="form-group">
                  <label>Subject</label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="" disabled>
                      Select an option...
                    </option>
                    <option value="General Enquiry">General Enquiry</option>
                    <option value="Course Booking">Course Booking</option>
                    <option value="CSCS Card Advice">CSCS Card Advice</option>
                    <option value="Corporate Training">
                      Corporate Training
                    </option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Message</label>
                <textarea
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="How can we help you today?"
                  rows="5"
                ></textarea>
              </div>

              {status.message && (
                <div className={`status-message ${status.type}`}>
                  {status.message}
                </div>
              )}

              <button
                type="submit"
                className="btn-primary submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
